import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import { Line } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import { apiRequest } from '../../../utils/api';
import { useEmpresaSelector } from '../../../context/EmpresaSelectorContext';
import '../../../styles/flujoCaja.css';

Chart.register(...registerables);

export function FlujoCaja() {
  const { empresaSeleccionada, esConexAdmin } = useEmpresaSelector();
  const [flujoCaja, setFlujoCaja] = useState({
    ingresos: {
      ventasEfectivo: {},
      ventasBanco: {}
    },
    egresos: {
      egresosEfectivo: {},
      egresosBanco: {}
    },
    financiamiento: {
      totalCobrar: {},
      totalPagar: {}
    }
  });

  const [editingCell, setEditingCell] = useState(null);
  const [tempValue, setTempValue] = useState("");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedWeek, setSelectedWeek] = useState(() => {
    // Calculate current week of the month (1-based)
    const today = new Date();
    return Math.ceil(today.getDate() / 7);
  });
  const [selectedDay, setSelectedDay] = useState('');
  const [dataVersion, setDataVersion] = useState(0); // Counter to force useMemo recalculation after async loads

  const mesesCompletos = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];

  // Helpers de fecha: usar SIEMPRE fecha local para evitar desfases de UTC
  const toLocalDateKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  // Cuando el backend envía cadenas ISO con zona horaria (ej: ...Z),
  // interpretamos la fecha en UTC para conservar el día original (evita irse al día anterior en UTC-5).
  const toUTCDateKey = (date) => {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const toLocalDateKeyFromISO = (isoString) => {
    if (!isoString) return '';
    // Si ya es YYYY-MM-DD, devolver tal cual
    if (/^\d{4}-\d{2}-\d{2}$/.test(isoString)) return isoString;
    // Si viene con zona horaria UTC (Z), convertir a local
    if (isoString.endsWith('Z')) {
      const date = new Date(isoString);
      const offset = date.getTimezoneOffset();
      const adjustedDate = new Date(date.getTime() + (offset * 60 * 1000));
      return adjustedDate.toISOString().split('T')[0];
    }
    // Caso general: extraer fecha
    return isoString.split('T')[0];
  };

  const parseDateKeyToLocalDate = (dateKey) => {
    if (!dateKey) return new Date(NaN);
    const [y, m, d] = dateKey.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  };

  // Load data from backend and localStorage
  useEffect(() => {
    const loadAll = async () => {
      await cargarDatosReales();
      await cargarCierresCaja();
      integrarDatosExistentes();
      // Increment version to force useMemo to recalculate with fresh localStorage data
      setDataVersion(v => v + 1);
    };
    loadAll();
  }, [selectedYear, selectedMonth, selectedWeek, empresaSeleccionada?.id]);

  // Cargar cierres de caja para conectar con CierreCaja.jsx
  const cargarCierresCaja = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No se encontró el token de autenticación');
        return;
      }
      
      const empresaId = esConexAdmin && empresaSeleccionada?.id && empresaSeleccionada.id !== 'all' ? empresaSeleccionada.id : null;
      const qs = empresaId ? `?empresaId=${empresaId}` : '';
      const response = await apiRequest(`/cierres-caja${qs}`);

      if (response.ok) {
        const data = await response.json();
        // Guardar en localStorage para uso en getAutoIngreso (conectado con CierreCaja.jsx)
        localStorage.setItem(`cierresCaja${empresaId ? '_' + empresaId : ''}`, JSON.stringify(data));
      }
    } catch (error) {
      console.error('Error cargando cierres de caja:', error);
    }
  };

  // Cargar datos reales desde el backend
  const cargarDatosReales = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No se encontró el token de autenticación');
        return;
      }
      
      const empresaId = esConexAdmin && empresaSeleccionada?.id && empresaSeleccionada.id !== 'all' ? empresaSeleccionada.id : null;
      const qs = empresaId ? `?empresaId=${empresaId}` : '';
      // Cargar ingresos desde el backend
      const responseIngresos = await apiRequest(`/ingresos${qs}`);

      if (responseIngresos.ok) {
        const dataIngresos = await responseIngresos.json();
        // Guardar en localStorage para uso en getAutoIngreso (por empresa)
        localStorage.setItem(`ingresos${empresaId ? '_' + empresaId : ''}`, JSON.stringify(dataIngresos));
      }

      // Cargar egresos desde el backend
      const responseEgresos = await apiRequest(`/egresos${qs}`);

      if (responseEgresos.ok) {
        const dataEgresos = await responseEgresos.json();
        // Guardar en localStorage para uso en getAutoEgreso (por empresa)
        localStorage.setItem(`egresos${empresaId ? '_' + empresaId : ''}`, JSON.stringify(dataEgresos));
      }

      // Cargar cuentas por cobrar
      const responseCxC = await apiRequest(`/cuentas-por-cobrar${qs}`);
      if (responseCxC.ok) {
        const dataCxC = await responseCxC.json();
        localStorage.setItem(`cxc${empresaId ? '_' + empresaId : ''}`, JSON.stringify(dataCxC));
      } else {
        console.error('❌ Error cargando CxC, status:', responseCxC.status);
      }

      // Cargar cuentas por pagar
      const responseCxP = await apiRequest(`/cuentas-por-pagar${qs}`);
      if (responseCxP.ok) {
        const dataCxP = await responseCxP.json();
        localStorage.setItem(`cxp${empresaId ? '_' + empresaId : ''}`, JSON.stringify(dataCxP));
      } else {
        console.error('❌ Error cargando CxP, status:', responseCxP.status);
      }
    } catch (error) {
      console.error('Error cargando datos reales:', error);
    }
  };

  // Integrate existing data (daily)
  const integrarDatosExistentes = () => {
    // Simulating data integration since we can't use localStorage
    const nuevosIngresos = { ...flujoCaja.ingresos };
    const nuevosEgresos = { ...flujoCaja.egresos };

    setFlujoCaja(prev => ({
      ...prev,
      ingresos: nuevosIngresos,
      egresos: nuevosEgresos
    }));
  };

  // Helpers por categoría (manual + automático) para evitar doble contabilidad
   const ingresoEfectivoDia = (dateKey) => (flujoCaja.ingresos?.ventasEfectivo?.[dateKey] || 0) + getAutoIngreso('ventasEfectivo', dateKey);
   const ingresoBancoDia = (dateKey) => (flujoCaja.ingresos?.ventasBanco?.[dateKey] || 0) + getAutoIngreso('ventasBanco', dateKey);
   const ingresoAbonoDia = (dateKey) => (flujoCaja.ingresos?.abono?.[dateKey] || 0) + getAutoIngreso('abono', dateKey);

  // Contadores de número de transacciones (conectado con CierreCaja.jsx)
   const numeroVentasDia = (dateKey) => {
     const ventasEfectivo = cierresCajaGlobales.filter(v => {
       const fechaISO = toLocalDateKeyFromISO(v.fecha || "");
       return fechaISO === dateKey && v.tipoPago === 'efectivo' && v.pagado > 0;
     }).length;

     const ventasBanco = cierresCajaGlobales.filter(v => {
       const fechaISO = toLocalDateKeyFromISO(v.fecha || "");
       return fechaISO === dateKey && v.tipoPago === 'transferencia' && v.pagado > 0;
     }).length;

     const abonos = cierresCajaGlobales.filter(v => {
       const fechaISO = toLocalDateKeyFromISO(v.fecha || "");
       return fechaISO === dateKey && v.tipoPago === 'porPagar' && v.abono > 0;
     }).length;

     return ventasEfectivo + ventasBanco + abonos;
   };

  const numeroEgresosDia = (dateKey) => {
    const efectivo = egresosGlobales.filter(e => {
      const fechaISO = toLocalDateKeyFromISO(e.fecha || "");
      return fechaISO === dateKey && e.tipoPago === 'efectivo';
    }).length;
    const banco = egresosGlobales.filter(e => {
      const fechaISO = toLocalDateKeyFromISO(e.fecha || "");
      return fechaISO === dateKey && e.tipoPago === 'transferencia';
    }).length;
    return efectivo + banco;
  };

  // Total Ingresos = Ventas Efectivo + Ventas Banco + Abono
   const calcularTotalIngresos = (dateKey) => ingresoEfectivoDia(dateKey) + ingresoBancoDia(dateKey) + ingresoAbonoDia(dateKey);

  // Egresos helpers
  const egresoEfectivoDia = (dateKey) => (flujoCaja.egresos?.egresosEfectivo?.[dateKey] || 0) + getAutoEgreso('egresosEfectivo', dateKey);
  const egresoBancoDia = (dateKey) => (flujoCaja.egresos?.egresosBanco?.[dateKey] || 0) + getAutoEgreso('egresosBanco', dateKey);

  // Total Egresos = Egresos Efectivo + Egresos Banco
  const calcularTotalEgresos = (dateKey) => egresoEfectivoDia(dateKey) + egresoBancoDia(dateKey);

  const calcularFlujoEconomico = (dateKey) => calcularTotalIngresos(dateKey) - calcularTotalEgresos(dateKey);

  const calcularTotalFinanciamiento = (dateKey) => {
    // Total a Cobrar (CxC) - Total a Pagar (CxP) + manual
    const totalCobrarAuto = getAutoFinanciamiento('totalCobrar', dateKey);
    const totalPagarAuto = getAutoFinanciamiento('totalPagar', dateKey);
    const manualCobrar = flujoCaja.financiamiento?.totalCobrar?.[dateKey] || 0;
    const manualPagar = flujoCaja.financiamiento?.totalPagar?.[dateKey] || 0;
    return (totalCobrarAuto + manualCobrar) - (totalPagarAuto + manualPagar);
  };

  const calcularFlujoFinanciero = (dateKey) => calcularFlujoEconomico(dateKey) + calcularTotalFinanciamiento(dateKey);

  // Función para calcular total de datos automáticos
   const calcularTotalAutomatico = (dateKey) => {
     let totalAuto = 0;

     // Ingresos automáticos
     totalAuto += getAutoIngreso("ventasEfectivo", dateKey);
     totalAuto += getAutoIngreso("ventasBanco", dateKey);
     totalAuto += getAutoIngreso("abono", dateKey);

     // Egresos automáticos
     totalAuto += getAutoEgreso("egresosEfectivo", dateKey);
     totalAuto += getAutoEgreso("egresosBanco", dateKey);

     return totalAuto;
   };

  // Get date keys for selected week
  const getDateKeysForWeek = (year, month, week) => {
    const dateKeys = [];
    const firstDayOfMonth = new Date(year, month, 1);
    const startOfWeek = new Date(firstDayOfMonth);
    startOfWeek.setDate(1 + (week - 1) * 7);

    for (let day = 0; day < 7; day++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + day);

      // Solo incluir días que pertenezcan al mes seleccionado
      if (date.getMonth() === month) {
        dateKeys.push(toLocalDateKey(date));
      }
    }

    return dateKeys;
  };

  const getWeeksInMonth = (year, month) => {
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return Math.ceil(daysInMonth / 7);
  };

  const dateKeys = getDateKeysForWeek(selectedYear, selectedMonth, selectedWeek);
  const totalWeeks = getWeeksInMonth(selectedYear, selectedMonth);
  

  // Asegurar día seleccionado válido al cambiar semana/mes/año
  useEffect(() => {
    if (!selectedDay || !dateKeys.includes(selectedDay)) {
      setSelectedDay(dateKeys[0] || '');
    }
  }, [selectedYear, selectedMonth, selectedWeek]);

  // Leer datos de ingresos y egresos desde localStorage usando useMemo para que se actualice cuando cambie la empresa
  const { empresaKey, ingresosGlobales, egresosGlobales, cxcGlobales, cxpGlobales, cierresCajaGlobales } = useMemo(() => {
    const key = esConexAdmin && empresaSeleccionada?.id && empresaSeleccionada.id !== 'all' ? `_${empresaSeleccionada.id}` : '';
    
    const ingresos = JSON.parse(localStorage.getItem(`ingresos${key}`)) || [];
    const egresos = JSON.parse(localStorage.getItem(`egresos${key}`)) || [];
    const cxc = JSON.parse(localStorage.getItem(`cxc${key}`)) || [];
    const cxp = JSON.parse(localStorage.getItem(`cxp${key}`)) || [];
    const cierresCaja = JSON.parse(localStorage.getItem(`cierresCaja${key}`)) || [];
    return {
      empresaKey: key,
      ingresosGlobales: ingresos,
      egresosGlobales: egresos,
      cxcGlobales: cxc,
      cxpGlobales: cxp,
      cierresCajaGlobales: cierresCaja
    };
  }, [empresaSeleccionada?.id, esConexAdmin, dataVersion]); // dataVersion ensures recalculation after async data loads

  // Función para obtener suma automática por fecha (conectado con CierreCaja.jsx)
   const getAutoIngreso = (subcategoria, dateKey) => {
     if (subcategoria === "ventasEfectivo") {
       const filtered = cierresCajaGlobales.filter(v => {
         const fechaISO = toLocalDateKeyFromISO(v.fecha || "");
         return fechaISO === dateKey && v.tipoPago === 'efectivo';
       });
       return filtered.reduce((sum, v) => sum + (parseFloat(v.pagado) || 0), 0);
     }
     if (subcategoria === "ventasBanco") {
       const filtered = cierresCajaGlobales.filter(v => {
         const fechaISO = toLocalDateKeyFromISO(v.fecha || "");
         return fechaISO === dateKey && v.tipoPago === 'transferencia';
       });
       return filtered.reduce((sum, v) => sum + (parseFloat(v.pagado) || 0), 0);
     }
     if (subcategoria === "abono") {
       const filtered = cierresCajaGlobales.filter(v => {
         const fechaISO = toLocalDateKeyFromISO(v.fecha || "");
         return fechaISO === dateKey && v.tipoPago === 'porPagar';
       });
       return filtered.reduce((sum, v) => sum + (parseFloat(v.abono) || 0), 0);
     }
     return 0;
   };

  // Función para obtener totales automáticos de financiamiento (CxC/CxP) por fecha
   const getAutoFinanciamiento = (subcategoria, dateKey) => {
     if (subcategoria === 'totalCobrar') {
       const filteredCxC = cxcGlobales.filter(c => {
         const fechaISO = toLocalDateKeyFromISO(c.fechaFactura || c.fecha || '');
         return fechaISO === dateKey;
       });
       const sum = filteredCxC.reduce((sum, c) => {
         const saldo = parseFloat(c.saldoCancelado || c.saldoAdeudado || 0);
         return sum + (Number.isFinite(saldo) ? saldo : 0);
       }, 0);
       return sum;
     }
     if (subcategoria === 'totalPagar') {
       const filteredCxP = cxpGlobales.filter(c => {
         const fechaISO = toLocalDateKeyFromISO(c.fechaFactura || c.fecha || '');
         return fechaISO === dateKey;
       });
       const sum = filteredCxP.reduce((sum, c) => {
         const saldo = parseFloat(c.saldoAdeudado || 0);
         return sum + (Number.isFinite(saldo) ? saldo : 0);
       }, 0);
       return sum;
     }
     return 0;
   };

  // Función para obtener suma automática de egresos por fecha (conectado con Egresos.jsx)
   const getAutoEgreso = (subcategoria, dateKey) => {
     if (subcategoria === "egresosEfectivo") {
       const filtered = egresosGlobales.filter(e => {
         const fechaISO = toLocalDateKeyFromISO(e.fecha || "");
         return fechaISO === dateKey && e.tipoPago === 'efectivo';
       });
       return filtered.reduce((sum, e) => sum + (parseFloat(e.monto) || 0), 0);
     }
     if (subcategoria === "egresosBanco") {
       const filtered = egresosGlobales.filter(e => {
         const fechaISO = toLocalDateKeyFromISO(e.fecha || "");
         return fechaISO === dateKey && e.tipoPago === 'transferencia';
       });
       return filtered.reduce((sum, e) => sum + (parseFloat(e.monto) || 0), 0);
     }
     return 0;
   };

  // Handle editing
  const iniciarEdicion = (categoria, subcategoria, dateKey, valor) => {
    setEditingCell(`${categoria}_${subcategoria}_${dateKey}`);
    setTempValue(valor.toString());
  };

  const guardarEdicion = () => {
    if (editingCell) {
      const [categoria, subcategoria, dateKey] = editingCell.split("_");
      const valor = parseFloat(tempValue) || 0;

      const nuevoFlujoCaja = { ...flujoCaja };
      
      // Asegurar que las propiedades existan
      if (!nuevoFlujoCaja[categoria]) {
        nuevoFlujoCaja[categoria] = {};
      }
      if (!nuevoFlujoCaja[categoria][subcategoria]) {
        nuevoFlujoCaja[categoria][subcategoria] = {};
      }
      
      nuevoFlujoCaja[categoria][subcategoria][dateKey] = valor;

      setFlujoCaja(nuevoFlujoCaja);
      setEditingCell(null);
      setTempValue("");
    }
  };

  const cancelarEdicion = () => {
    setEditingCell(null);
    setTempValue("");
  };

  // Export to Excel
  const exportarExcel = () => {
    const datos = [];

    // Headers
    datos.push(["Concepto", ...dateKeys.map(dateKey => {
      const d = parseDateKeyToLocalDate(dateKey);
      return d.getDate();
    }), "Total"]);

    // Ingresos
     datos.push(["INGRESOS"]);
     // Número de Ventas
     const numeroVentasValues = dateKeys.map(dateKey => numeroVentasDia(dateKey));
     const numeroVentasTotal = numeroVentasValues.reduce((sum, val) => sum + val, 0);
     datos.push(["Número de Ventas", ...numeroVentasValues, numeroVentasTotal]);
     // Incluir las subcategorías de ingresos
     const subcategoriasIngresos = ["ventasEfectivo", "ventasBanco", "abono"];
     subcategoriasIngresos.forEach(key => {
       const valores = flujoCaja.ingresos?.[key] || {};
       const label = key === "ventasEfectivo" ? "Ventas en Efectivo" :
                     key === "ventasBanco" ? "Ventas en Banco" :
                     key === "abono" ? "Abono" : key.replace(/([A-Z])/g, " $1").trim();
       const values = dateKeys.map(dateKey => (valores[dateKey] || 0) + getAutoIngreso(key, dateKey));
       const total = values.reduce((sum, val) => sum + val, 0);
       datos.push([label, ...values, total]);
     });

    // Total Ingresos
    const totalIngresosValues = dateKeys.map(dateKey => calcularTotalIngresos(dateKey));
    const totalIngresosTotal = totalIngresosValues.reduce((sum, val) => sum + val, 0);
    datos.push(["Total Ingresos", ...totalIngresosValues, totalIngresosTotal]);

    // Egresos
    datos.push(["EGRESOS"]);
    // Número de Egresos
    const numeroEgresosValues = dateKeys.map(dateKey => numeroEgresosDia(dateKey));
    const numeroEgresosTotal = numeroEgresosValues.reduce((sum, val) => sum + val, 0);
    datos.push(["Número de Egresos", ...numeroEgresosValues, numeroEgresosTotal]);
    Object.entries(flujoCaja.egresos).forEach(([key, valores]) => {
      const label = key === "egresosEfectivo" ? "Egresos en Efectivo" :
                    key === "egresosBanco" ? "Egresos en Banco" : key.replace(/([A-Z])/g, " $1").trim();
      const values = dateKeys.map(dateKey => valores[dateKey] || 0);
      const total = values.reduce((sum, val) => sum + val, 0);
      datos.push([label, ...values, total]);
    });

    // Total Egresos
    const totalEgresosValues = dateKeys.map(dateKey => calcularTotalEgresos(dateKey));
    const totalEgresosTotal = totalEgresosValues.reduce((sum, val) => sum + val, 0);
    datos.push(["Total Egresos", ...totalEgresosValues, totalEgresosTotal]);

    // Flujo de caja económico
    const flujoEconomicoValues = dateKeys.map(dateKey => calcularFlujoEconomico(dateKey));
    const flujoEconomicoTotal = flujoEconomicoValues.reduce((sum, val) => sum + val, 0);
    datos.push(["Flujo de caja económico", ...flujoEconomicoValues, flujoEconomicoTotal]);

    // Financiamiento
    datos.push(["FINANCIAMIENTO"]);
    const financiamientoKeys = ['totalCobrar', 'totalPagar'];
    financiamientoKeys.forEach((key) => {
      const label = key === 'totalCobrar' ? 'Total a Cobrar (CxC)' : 'Total a Pagar (CxP)';
      const values = dateKeys.map(dateKey => {
        const manual = flujoCaja.financiamiento?.[key]?.[dateKey] || 0;
        const auto = getAutoFinanciamiento(key, dateKey);
        return manual + auto;
      });
      const total = values.reduce((sum, val) => sum + val, 0);
      datos.push([label, ...values, total]);
    });


    // Flujo de caja financiero
    const flujoFinancieroValues = dateKeys.map(dateKey => calcularFlujoFinanciero(dateKey));
    const flujoFinancieroTotal = flujoFinancieroValues.reduce((sum, val) => sum + val, 0);
    datos.push(["Flujo de caja financiero", ...flujoFinancieroValues, flujoFinancieroTotal]);

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(datos);
         XLSX.utils.book_append_sheet(wb, ws, `FlujoCaja_Semana${selectedWeek}_${mesesCompletos[selectedMonth]}_${selectedYear}`);
     XLSX.writeFile(wb, `FlujoCaja_Semana${selectedWeek}_${mesesCompletos[selectedMonth]}_${selectedYear}.xlsx`);
  };

  // Exportar Excel por día seleccionado
  const exportarExcelDia = () => {
    if (!selectedDay) return;
    const d = selectedDay;
    const rows = [];
    rows.push(["Concepto", "Valor"]);
    // Ingresos (manual + automático)
     rows.push(["Número de Ventas", numeroVentasDia(d)]);
     rows.push(["Ventas en Efectivo", Number(ingresoEfectivoDia(d).toFixed(2))]);
     rows.push(["Ventas en Banco", Number(ingresoBancoDia(d).toFixed(2))]);
     rows.push(["Abono", Number(ingresoAbonoDia(d).toFixed(2))]);
     rows.push(["Total Ingresos", Number(calcularTotalIngresos(d).toFixed(2))]);
    // Egresos
    rows.push(["Número de Egresos", numeroEgresosDia(d)]);
    rows.push(["Egresos en Efectivo", Number(egresoEfectivoDia(d).toFixed(2))]);
    rows.push(["Egresos en Banco", Number(egresoBancoDia(d).toFixed(2))]);
    rows.push(["Total Egresos", Number(calcularTotalEgresos(d).toFixed(2))]);
    // Financiamiento
    const totalCobrar = getAutoFinanciamiento('totalCobrar', d) + (flujoCaja.financiamiento?.totalCobrar?.[d] || 0);
    const totalPagar = getAutoFinanciamiento('totalPagar', d) + (flujoCaja.financiamiento?.totalPagar?.[d] || 0);
    rows.push(["Total a Cobrar (CxC)", Number(totalCobrar.toFixed(2))]);
    rows.push(["Total a Pagar (CxP)", Number(totalPagar.toFixed(2))]);
    // Flujos
    rows.push(["Flujo de caja económico", Number(calcularFlujoEconomico(d).toFixed(2))]);
    rows.push(["Flujo de caja financiero", Number(calcularFlujoFinanciero(d).toFixed(2))]);
    const wsDia = XLSX.utils.aoa_to_sheet(rows);
    const wbDia = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wbDia, wsDia, `Dia_${d}`);
    XLSX.writeFile(wbDia, `FlujoCaja_${d}.xlsx`);
  };

  // Editable cell component
   const CeldaEditable = ({ categoria, subcategoria, dateKey, valor }) => {
     const cellId = `${categoria}_${subcategoria}_${dateKey}`;
     const isEditing = editingCell === cellId;
     const auto = categoria === "ingresos" ? getAutoIngreso(subcategoria, dateKey) :
                  categoria === "egresos" ? getAutoEgreso(subcategoria, dateKey) :
                  categoria === "financiamiento" ? getAutoFinanciamiento(subcategoria, dateKey) : 0;
     const total = (valor || 0) + auto;

     // Para la fila de Abono, mostrar solo el valor automático (no editable)
     if (subcategoria === "abono") {
       return (
         <span className="flujo-caja-editable-cell">
           <span className="flujo-caja-value">${total.toFixed(2)}</span>
         </span>
       );
     }

    // Verificar que las propiedades existan antes de acceder a ellas
    if (!flujoCaja[categoria] || !flujoCaja[categoria][subcategoria]) {
      return (
        <span
          className="flujo-caja-editable-cell"
          onClick={() => iniciarEdicion(categoria, subcategoria, dateKey, 0)}
        >
          <span className="flujo-caja-value">${total.toFixed(2)}</span>
        </span>
      );
    }

    if (isEditing) {
             return (
         <input
           type="number"
           className="flujo-caja-input"
           value={tempValue}
           onChange={(e) => setTempValue(e.target.value)}
           onBlur={guardarEdicion}
           onKeyDown={(e) => {
             if (e.key === "Enter") guardarEdicion();
             if (e.key === "Escape") cancelarEdicion();
           }}
           autoFocus
         />
       );
    }

         return (
       <span
         className="flujo-caja-editable-cell"
         onClick={() => iniciarEdicion(categoria, subcategoria, dateKey, valor)}
       >
         <span className="flujo-caja-value">${total.toFixed(2)}</span>
       </span>
     );
  };

  return (
    <div className="flujocaja-container">
      {/* Navegación de pestañas */}
      <div className="tabs is-centered is-boxed mb-5">
        <ul>
          <li className="is-active">
            <a href="/dashboard/reporte/flujo-caja">
              <span className="icon is-small"><i className="fas fa-exchange-alt" aria-hidden="true"></i></span>
              <span>Flujo de Caja</span>
            </a>
          </li>
          <li>
            <a href="/dashboard/reporte/reporte">
              <span className="icon is-small"><i className="fas fa-file-alt" aria-hidden="true"></i></span>
              <span>Reporte</span>
            </a>
          </li>
          <li>
            <a href="/dashboard/reporte/reporte-general">
              <span className="icon is-small"><i className="fas fa-chart-bar" aria-hidden="true"></i></span>
              <span>Reporte General</span>
            </a>
          </li>
          <li>
            <a href="/dashboard/reporte/resumen-total">
              <span className="icon is-small"><i className="fas fa-calculator" aria-hidden="true"></i></span>
              <span>Resumen Total</span>
            </a>
          </li>
        </ul>
      </div>

      <h1 className="flujocaja-title">Flujo de Caja</h1>

      {/* Controls */}
      <div className="flujo-caja-controls">
        <div className="flujo-caja-filters">
          <div className="flujo-caja-filter-group">
            <label className="flujo-caja-label">Año:</label>
            <select
              className="flujo-caja-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            >
              {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <div className="flujo-caja-filter-group">
            <label className="flujo-caja-label">Mes:</label>
            <select
              className="flujo-caja-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            >
              {mesesCompletos.map((mes, index) => (
                <option key={index} value={index}>
                  {mes}
                </option>
              ))}
            </select>
          </div>
          <div className="flujo-caja-filter-group">
            <label className="flujo-caja-label">Semana:</label>
            <select
              className="flujo-caja-select"
              value={selectedWeek}
              onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
            >
              {Array.from({ length: totalWeeks }, (_, i) => i + 1).map(week => (
                <option key={week} value={week}>
                  Semana {week}
                </option>
              ))}
            </select>
          </div>
          <div className="flujo-caja-filter-group">
            <label className="flujo-caja-label">Día:</label>
            <select
              className="flujo-caja-select"
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
            >
              {dateKeys.map(d => {
                const date = parseDateKeyToLocalDate(d);
                const diasSemana = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
                const diaSemana = diasSemana[date.getDay()];
                return (
                  <option key={d} value={d}>{diaSemana} {date.getDate()}/{selectedMonth+1}</option>
                );
              })}
            </select>
          </div>
        </div>
        <button
          className="flujo-caja-export-btn"
          onClick={exportarExcel}
        >
          Exportar Excel
        </button>
        <button
          className="flujo-caja-export-btn"
          onClick={exportarExcelDia}
          style={{ marginLeft: 8 }}
        >
          Excel por día
        </button>
      </div>

      {/* Table Container with proper centering */}
      <div className="flujo-caja-table-container">
        <table className="flujo-caja-table">
          <thead>
            <tr className="has-background-gold has-text-darkblue">
              <th className="flujo-caja-th-concepto">Concepto</th>
              {dateKeys.map(dateKey => {
                const date = parseDateKeyToLocalDate(dateKey);
                const diasSemana = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
                const diaSemana = diasSemana[date.getDay()];
                return (
                  <th key={dateKey} className="flujo-caja-th-day">
                    {diaSemana}<br/>{date.getDate()}/{date.getMonth() + 1}
                  </th>
                );
              })}
              <th className="flujo-caja-th-total">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {/* INGRESOS */}
            <tr>
              <td className="flujo-caja-section-header">
                INGRESOS
              </td>
              <td colSpan={dateKeys.length + 1}></td>
            </tr>
            {/* Número de Ventas (solo informativo, no se suma) */}
            <tr>
              <td className="flujo-caja-td-concepto">Número de Ventas</td>
              {dateKeys.map(dateKey => (
                <td key={dateKey} className="flujo-caja-td-day">
                  {numeroVentasDia(dateKey)}
                </td>
              ))}
              <td className="flujo-caja-td-total">
                {dateKeys.map(dateKey => numeroVentasDia(dateKey)).reduce((sum, val) => sum + val, 0)}
              </td>
            </tr>
            {["ventasEfectivo", "ventasBanco", "abono"].map(subcategoria => {
              const label = subcategoria === "ventasEfectivo" ? "Ventas en Efectivo" :
                            subcategoria === "ventasBanco" ? "Ventas en Banco" :
                            subcategoria === "abono" ? "Abono" : subcategoria.replace(/([A-Z])/g, " $1").trim();
              const total = dateKeys.map(dateKey => {
                const manualValue = flujoCaja.ingresos?.[subcategoria]?.[dateKey] || 0;
                const autoValue = getAutoIngreso(subcategoria, dateKey);
                return manualValue + autoValue;
              }).reduce((sum, val) => sum + val, 0);
              return (
                <tr key={subcategoria}>
                  <td className="flujo-caja-td-concepto">{label}</td>
                  {dateKeys.map(dateKey => (
                    <td key={dateKey} className="flujo-caja-td-day">
                      <CeldaEditable
                        categoria="ingresos"
                        subcategoria={subcategoria}
                        dateKey={dateKey}
                        valor={flujoCaja.ingresos?.[subcategoria]?.[dateKey] || 0}
                      />
                    </td>
                  ))}
                  <td className="flujo-caja-td-total">
                    ${total.toFixed(2)}
                  </td>
                </tr>
              );
            })}
            <tr className="flujo-caja-total-row">
              <td className="flujo-caja-td-concepto">Total Ingresos</td>
              {dateKeys.map(dateKey => (
                <td key={dateKey} className="flujo-caja-td-day">
                  ${calcularTotalIngresos(dateKey).toFixed(2)}
                </td>
              ))}
              <td className="flujo-caja-td-total">
                ${dateKeys.map(dateKey => calcularTotalIngresos(dateKey)).reduce((sum, val) => sum + val, 0).toFixed(2)}
              </td>
            </tr>

            {/* EGRESOS */}
            <tr>
              <td className="flujo-caja-section-header">
                EGRESOS
              </td>
              <td colSpan={dateKeys.length + 1}></td>
            </tr>
            {/* Número de Egresos (solo informativo, no se suma) */}
            <tr>
              <td className="flujo-caja-td-concepto">Número de Egresos</td>
              {dateKeys.map(dateKey => (
                <td key={dateKey} className="flujo-caja-td-day">
                  {numeroEgresosDia(dateKey)}
                </td>
              ))}
              <td className="flujo-caja-td-total">
                {dateKeys.map(dateKey => numeroEgresosDia(dateKey)).reduce((sum, val) => sum + val, 0)}
              </td>
            </tr>
            {["egresosEfectivo", "egresosBanco"].map(subcategoria => {
              const label = subcategoria === "egresosEfectivo" ? "Egresos en Efectivo" :
                            subcategoria === "egresosBanco" ? "Egresos en Banco" : subcategoria.replace(/([A-Z])/g, " $1").trim();
              const total = dateKeys.map(dateKey => {
                const manualValue = flujoCaja.egresos?.[subcategoria]?.[dateKey] || 0;
                const autoValue = getAutoEgreso(subcategoria, dateKey);
                return manualValue + autoValue;
              }).reduce((sum, val) => sum + val, 0);
              return (
                <tr key={subcategoria}>
                  <td className="flujo-caja-td-concepto">{label}</td>
                  {dateKeys.map(dateKey => (
                    <td key={dateKey} className="flujo-caja-td-day">
                      <CeldaEditable
                        categoria="egresos"
                        subcategoria={subcategoria}
                        dateKey={dateKey}
                        valor={flujoCaja.egresos?.[subcategoria]?.[dateKey] || 0}
                      />
                    </td>
                  ))}
                  <td className="flujo-caja-td-total">
                    ${total.toFixed(2)}
                  </td>
                </tr>
              );
            })}
            <tr className="flujo-caja-total-row">
              <td className="flujo-caja-td-concepto">Total Egresos</td>
              {dateKeys.map(dateKey => (
                <td key={dateKey} className="flujo-caja-td-day">
                  ${calcularTotalEgresos(dateKey).toFixed(2)}
                </td>
              ))}
              <td className="flujo-caja-td-total">
                ${dateKeys.map(dateKey => calcularTotalEgresos(dateKey)).reduce((sum, val) => sum + val, 0).toFixed(2)}
              </td>
            </tr>

            <tr className="flujo-caja-economic-row">
              <td className="flujo-caja-td-concepto">Flujo de caja económico</td>
              {dateKeys.map(dateKey => (
                <td key={dateKey} className="flujo-caja-td-day">
                  ${calcularFlujoEconomico(dateKey).toFixed(2)}
                </td>
              ))}
              <td className="flujo-caja-td-total">
                ${dateKeys.map(dateKey => calcularFlujoEconomico(dateKey)).reduce((sum, val) => sum + val, 0).toFixed(2)}
              </td>
            </tr>

            {/* FINANCIAMIENTO (conectado a CxC/CxP) */}
            <tr>
              <td className="flujo-caja-section-header">
                FINANCIAMIENTO
              </td>
              <td colSpan={dateKeys.length + 1}></td>
            </tr>
            {['totalCobrar', 'totalPagar'].map(subcategoria => {
              const label = subcategoria === 'totalCobrar' ? 'Total a Cobrar (CxC)' : 'Total a Pagar (CxP)';
              const total = dateKeys.map(dateKey => {
                const manual = flujoCaja.financiamiento?.[subcategoria]?.[dateKey] || 0;
                const auto = getAutoFinanciamiento(subcategoria, dateKey);
                return manual + auto;
              }).reduce((sum, val) => sum + val, 0);
              return (
                <tr key={subcategoria}>
                  <td className="flujo-caja-td-concepto">{label}</td>
                  {dateKeys.map(dateKey => (
                    <td key={dateKey} className="flujo-caja-td-day">
                      <CeldaEditable
                        categoria="financiamiento"
                        subcategoria={subcategoria}
                        dateKey={dateKey}
                        valor={flujoCaja.financiamiento?.[subcategoria]?.[dateKey] || 0}
                      />
                    </td>
                  ))}
                  <td className="flujo-caja-td-total">
                    ${total.toFixed(2)}
                  </td>
                </tr>
              );
            })}

            <tr className="flujo-caja-economic-row">
              <td className="flujo-caja-td-concepto">Flujo de caja financiero</td>
              {dateKeys.map(dateKey => (
                <td key={dateKey} className="flujo-caja-td-day">
                  ${calcularFlujoFinanciero(dateKey).toFixed(2)}
                </td>
              ))}
              <td className="flujo-caja-td-total">
                ${dateKeys.map(dateKey => calcularFlujoFinanciero(dateKey)).reduce((sum, val) => sum + val, 0).toFixed(2)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Resumen Mensual */}
        <div className="flujo-caja-summary">
        <div className="flujo-caja-summary-box flujo-caja-summary-gold">
          <h4 className="flujo-caja-summary-title">Resumen Mensual</h4>
          <p className="flujo-caja-summary-text"><strong>Mes:</strong> {mesesCompletos[selectedMonth]} {selectedYear}</p>
          <p className="flujo-caja-summary-text"><strong>Semana seleccionada:</strong> {selectedWeek} de {totalWeeks}</p>
          <p className="flujo-caja-summary-text"><strong>Total Ingresos:</strong> ${dateKeys.map(dateKey => calcularTotalIngresos(dateKey)).reduce((sum, val) => sum + val, 0).toFixed(2)}</p>
          <p className="flujo-caja-summary-text"><strong>Total Egresos:</strong> ${dateKeys.map(dateKey => calcularTotalEgresos(dateKey)).reduce((sum, val) => sum + val, 0).toFixed(2)}</p>
          <p className="flujo-caja-summary-text"><strong>Flujo Neto:</strong> ${dateKeys.map(dateKey => calcularFlujoFinanciero(dateKey)).reduce((sum, val) => sum + val, 0).toFixed(2)}</p>
          <p className="flujo-caja-summary-text"><strong>Datos Automáticos:</strong> ${dateKeys.map(dateKey => calcularTotalAutomatico(dateKey)).reduce((sum, val) => sum + val, 0).toFixed(2)}</p>
        </div>
      </div>
    </div>
  );
}

export default FlujoCaja;
